import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { fileQueryKeys } from "@/shared/domain/files/hooks/fileQueryKeys";
import { uploadOrgFile } from "@/shared/domain/files/uploadOrgFile";
import { userFacingErrorMessage } from "@/shared/http";

export interface ThreadAttachment {
  /** Identifies the entry in the list; two entries may share a name. */
  id: string;
  /** The stored name, as `attachmentName` gives it. */
  name: string;
  status: "uploading" | "uploaded" | "failed";
  error?: React.ReactNode;
}

/**
 * `message` followed by one `[file: <name>]` line per uploaded attachment, in
 * list order. Those lines are the only record of the attachments in the
 * transcript, and the name in each is what the agent passes to `load_file`.
 */
export function withAttachmentLines(
  message: string,
  attachments: ThreadAttachment[],
): string {
  const lines = attachments
    .filter((a) => a.status === "uploaded")
    .map((a) => `[file: ${a.name}]`);
  // A message of only whitespace would otherwise send as a blank line above the names.
  const text = lines.length === 0 ? message : message.trimEnd();
  return [text, ...lines].filter((line) => line !== "").join("\n");
}

/**
 * The name an attachment is stored under and named by in the message, or why
 * the file's name cannot be one. The same name goes into the message's
 * `[file: <name>]` line and the stored path, so the agent's lookup matches
 * the stored file byte for byte: the name is composed (Unicode NFC) and
 * trimmed, and must fit on that one line. Nothing else is altered, since a
 * renamed file would surprise the user.
 */
export function attachmentName(
  fileName: string,
): { name: string } | { error: string } {
  const name = fileName.normalize("NFC").trim();
  if (name.includes("]")) {
    return { error: 'File names cannot contain "]".' };
  }
  if (/[\n\r\u2028\u2029]/.test(name)) {
    return { error: "File names cannot contain line breaks." };
  }
  if (/\p{Cc}/u.test(name)) {
    return { error: "File names cannot contain control characters." };
  }
  return { name };
}

/**
 * Whether a paste or drag carries files and nothing else. A transfer whose
 * `types` hold `"text/plain"` beside `"Files"`, such as spreadsheet cells
 * copied with their rendering, is text to the composer and is left for the
 * textarea to handle. Takes the `types` alone because a drag-over event
 * exposes them before the files themselves.
 */
export function isFilesOnlyTransfer(types: readonly string[]): boolean {
  return types.includes("Files") && !types.includes("text/plain");
}

/** The files of a paste or drop that `isFilesOnlyTransfer` admits, else none. */
export function filesFromTransfer(transfer: {
  types: readonly string[];
  files: ArrayLike<File>;
}): File[] {
  return isFilesOnlyTransfer(transfer.types) ? Array.from(transfer.files) : [];
}

/**
 * The files attached to the next message of thread `threadId`. Each file is
 * uploaded as soon as it is added, as one of the org's own files (associated
 * with the org rather than a dataset) at `threads/<thread id>/attachments/<name>`,
 * so the message can name it once sent.
 */
export function useThreadAttachments(
  orgId: string | undefined,
  threadId: string | undefined,
) {
  const queryClient = useQueryClient();
  const [attachments, setAttachments] = React.useState<ThreadAttachment[]>([]);

  // The list belongs to one thread, so opening another chat drops it.
  React.useEffect(() => setAttachments([]), [threadId]);

  // Only updates an entry still in the list, so a file removed mid-upload, or
  // dropped with its thread, does not reappear when its upload settles.
  const settle = (id: string, outcome: Partial<ThreadAttachment>) =>
    setAttachments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...outcome } : a)),
    );

  const add = (files: Iterable<File>) => {
    if (orgId === undefined || threadId === undefined) {
      return;
    }
    // A file that cannot be attached is listed as failed with its error, so
    // the user sees why and can remove it; nothing is uploaded for it.
    const addFailed = (id: string, name: string, error: string) =>
      setAttachments((prev) => [
        ...prev,
        { id, name, status: "failed", error },
      ]);
    const taken = new Set(attachments.map((a) => a.name));
    for (const file of files) {
      const id = crypto.randomUUID();
      const checked = attachmentName(file.name);
      if ("error" in checked) {
        addFailed(id, file.name, checked.error);
        continue;
      }
      const { name } = checked;
      // A second file under a name already in the list is rejected, not renamed
      // (see `attachmentName`). The same name attached in a later message is
      // allowed and becomes a new version of the first.
      if (taken.has(name)) {
        addFailed(id, name, `A file named ${name} is already attached.`);
        continue;
      }
      taken.add(name);
      setAttachments((prev) => [...prev, { id, name, status: "uploading" }]);
      uploadOrgFile(file, orgId, `threads/${threadId}/attachments/${name}`)
        .then(() => {
          settle(id, { status: "uploaded" });
          // Refreshes an open Assets page, which lists the org's files under
          // this key.
          void queryClient.invalidateQueries({
            queryKey: fileQueryKeys.directoryContents({ associationId: orgId }),
          });
        })
        .catch((error: unknown) =>
          settle(id, {
            status: "failed",
            error: userFacingErrorMessage(error),
          }),
        );
    }
  };

  const remove = (id: string) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  const clear = () => setAttachments([]);

  return {
    attachments,
    add,
    remove,
    clear,
    /**
     * Every entry is uploaded (none failed or in flight), so a message naming
     * them all can be sent.
     */
    allUploaded: attachments.every((a) => a.status === "uploaded"),
  };
}
